import { useState, useEffect, useCallback, createContext, useContext } from "react";
import useUserId from "hooks/useUserId";

// Create context for saved posts
const SavedPostsContext = createContext(null);

// Provider component
export const SavedPostsProvider = ({ children }) => {
  const [savedPosts, setSavedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const user = useUserId();
  const userId = user.userId;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch all saved posts once
  const fetchSavedPosts = useCallback(async () => {
    if (!userId) return;
    
    try {
      setIsLoading(true);
      
      // Fetch regular saved posts
      const postsResponse = await fetch(
        `${apiUrl}candidates/list_saved_posts/${userId}/`
      );

      if (!postsResponse.ok) {
        throw new Error(`Failed to fetch saved posts: ${postsResponse.statusText}`);
      }

      const postsData = await postsResponse.json();
      
      // Fetch saved spontaneous applications
      let spontaneousData = [];
      try {
        const spontaneousResponse = await fetch(
          `${apiUrl}candidates/list_saved_spontaneous/${userId}/`
        );
        
        if (spontaneousResponse.ok) {
          spontaneousData = await spontaneousResponse.json();
          // Transform spontaneous data to match the expected format
          spontaneousData = spontaneousData.map(item => ({
            post: { id: item.id },
            isSpontaneous: true
          }));
        }
      } catch (error) {
        console.error("Error fetching saved spontaneous applications:", error);
        // Continue with regular posts if spontaneous fetch fails
      }

      // Combine both types of saved items
      setSavedPosts([...postsData, ...spontaneousData]);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching saved posts:", err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, apiUrl]);

  // Initial fetch
  useEffect(() => {
    if (userId) {
      fetchSavedPosts();
    }
  }, [userId, fetchSavedPosts]);

  // Update saved posts when a post is saved/unsaved
  const updateSavedPosts = useCallback((jobId, isSaved) => {
    if (isSaved) {
      setSavedPosts(prev => [...prev, { post: { id: jobId } }]);
    } else {
      setSavedPosts(prev => prev.filter(item => item.post?.id !== jobId));
    }
  }, []);

  return (
    <SavedPostsContext.Provider value={{ savedPosts, isLoading, error, updateSavedPosts }}>
      {children}
    </SavedPostsContext.Provider>
  );
};

// Hook to use saved posts context
export const useSavedPosts = () => {
  const context = useContext(SavedPostsContext);
  if (!context) {
    throw new Error('useSavedPosts must be used within a SavedPostsProvider');
  }
  return context;
};

// Hook for individual job save status
export const useSaveStatus = ({ jobId, onSaveChange, type = 'post', initialSaved }) => {
  const [isSaved, setIsSaved] = useState(initialSaved === true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const user = useUserId();
  const userId = user.userId;
  const apiUrl = process.env.REACT_APP_API_URL;
  const { savedPosts, updateSavedPosts } = useSavedPosts();

  // Check if job is saved using the shared saved posts list
  useEffect(() => {
    // Only use the saved posts list if initialSaved wasn't provided
    if (initialSaved === undefined && savedPosts && jobId) {
      const isJobSaved = savedPosts.some(savedPost => savedPost.post.id === jobId);
      setIsSaved(isJobSaved);
    }
  }, [savedPosts, jobId, initialSaved]);

  const toggleSaveStatus = useCallback(async () => {
    if (!userId || !jobId) return;

    // Determine which endpoint to use based on type
    const endpoint = type === 'spontaneous' 
      ? `${apiUrl}candidates/save_spontaneous/${userId}/${jobId}/`
      : `${apiUrl}candidates/save_post/${userId}/${jobId}/`;

    try {
      setIsLoading(true);
      
      const response = await fetch(
        endpoint,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${isSaved ? 'unsave' : 'save'} ${type}`);
      }

      // Update local state
      setIsSaved(!isSaved);
      
      // Update shared saved posts list
      updateSavedPosts(jobId, !isSaved);

      if (onSaveChange) {
        onSaveChange(!isSaved);
      }
    } catch (err) {
      setError(err.message);
      console.error(`Error ${isSaved ? 'unsaving' : 'saving'} ${type}:`, err);
    } finally {
      setIsLoading(false);
    }
  }, [userId, jobId, isSaved, onSaveChange, apiUrl, updateSavedPosts, type]);

  return {
    isSaved,
    isLoading,
    error,
    toggleSaveStatus
  };
};